import { useEffect, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight client-side A/B test hook.
 *
 * - Deterministically assigns a variant per browser via localStorage.
 * - Logs an `impression` event the first time a user sees the test in a session.
 * - Exposes `trackConversion(metadata?)` to log a conversion when the goal happens.
 *
 * Events are stored in the `ab_test_events` table (anyone can insert; admins read).
 */
export function useABTest<V extends string>(
  testName: string,
  variants: readonly V[],
) {
  const variant = useMemo<V>(() => {
    if (typeof window === "undefined") return variants[0];
    const key = `ab:${testName}`;
    const existing = window.localStorage.getItem(key) as V | null;
    if (existing && variants.includes(existing)) return existing;
    const picked = variants[Math.floor(Math.random() * variants.length)];
    window.localStorage.setItem(key, picked);
    return picked;
  }, [testName, variants]);

  const sessionId = useMemo(() => {
    if (typeof window === "undefined") return "ssr";
    const key = "ab:session_id";
    let sid = window.sessionStorage.getItem(key);
    if (!sid) {
      sid = crypto.randomUUID();
      window.sessionStorage.setItem(key, sid);
    }
    return sid;
  }, []);

  const impressionLogged = useRef(false);

  useEffect(() => {
    if (impressionLogged.current) return;
    impressionLogged.current = true;
    const key = `ab:impression:${testName}:${sessionId}`;
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, "1");
    void supabase.from("ab_test_events").insert([{
      test_name: testName,
      variant,
      event_type: "impression",
      session_id: sessionId,
      user_agent: navigator.userAgent,
    }]);
  }, [testName, variant, sessionId]);

  const trackConversion = (metadata: Record<string, unknown> = {}) => {
    void supabase.from("ab_test_events").insert([{
      test_name: testName,
      variant,
      event_type: "conversion",
      session_id: sessionId,
      user_agent: navigator.userAgent,
      metadata: metadata as Record<string, string | number | boolean | null>,
    }]);
  };

  return { variant, trackConversion, sessionId };
}